import React from "react";
import { useAppContext } from "@components/Provider";
import NextImage from "@components/Image/NextImage";
import PostImage from "@components/PostImage/PostImage";
import dynamic from "next/dynamic";
import PromoRibbon from "@components/PromoRibbon/PromoRibbon";
import AuthorSlider from "@components/AuthorSlider/AuthorSlider";
import BookSlider from "@components/BookSlider/BookSliderComponent";

import styles from "./PageBuilder.module.css";
import "./PageBuilder.css";

//Dynamic Imports
const BookListicle = dynamic(() => import("../components/BookListicle/BookListicle"));
const CardSlider = dynamic(() => import("../components/CardSlider/CardSlider"));
const HomePageStories = dynamic(() => import("../components/HomePageStories/HomePageStories"));
const Deals = dynamic(() => import("@components/HomePage/CatalogDeals"), {
  ssr: true,
});

const AffiliateButton = dynamic(() => import("../components/AffiliateButton/AffiliateButton"));
const Video = dynamic(() => import("../components/Video/Video"));
const Embed = dynamic(() => import("../components/Embed/Embed"));
const BookInfoBatch = dynamic(() => import("../components/BookInfoBatch/BookInfoBatch"));
const Content = dynamic(() => import("../components/Content/Content"));
const PostHeader = dynamic(() => import("../components/PostHeader/PostHeader"));
const SubHeader = dynamic(() => import("../components/SubHeader/SubHeader"));
const Interview = dynamic(() => import("../components/Interview/Interview"));
const PullQuote = dynamic(() => import("../components/PullQuote/PullQuote"));
const Listicle = dynamic(() => import("../components/Listicle/Listicle"));
const BookInfo = dynamic(() => import("../components/BookInfo/BookInfo"));
const InlineQuotes = dynamic(() => import("../components/InlineQuotes/InlineQuotes"));
const RecommendationEnginePromos = dynamic(
  () => import("../components/RecommendationEnginePromos/RecommendationEnginePromos")
);
const DealsContent = dynamic(() => import("../components/DealsContent/DealsContent"));
const ManualAd = dynamic(() => import("../components/Ads/ManualAd"));
const RelatedLink = dynamic(() => import("../components/RelatedLink/RelatedLink"));
const NewsletterV2 = dynamic(() => import("../components/NewsletterV2/NewsletterV2"));
const NewsletterV2Modal = dynamic(() => import("../components/NewsletterV2/NewsletterV2Modal"));
const NewsletterV2WithImage = dynamic(
  () => import("../components/NewsletterV2/NewsletterV2WithImage")
);

const PageBuilder = (props) => {
  let {
    post,
    pageBuilderOptions,
    siteId,
    isMobile,
    isTablet,
    adNetwork,
    pageUrl,
    classes,
    dealsData,
  } = props;

  return (
    <>
      {BuildPage(
        post,
        pageBuilderOptions,
        siteId,
        isMobile,
        adNetwork,
        pageUrl,
        isTablet,
        classes,
        dealsData
      )}
    </>
  );
};

const CalculateAdsAfterNumberOfComponent = (totalComponent) => {
  //If total component is < 5, we shouldn't insert any manual ad
  if (totalComponent < 5) return -1;
  let divider = 5;
  if (totalComponent) {
    //Updated from < 10 to < 100 based on https://openroadmedia.atlassian.net/browse/ORMTECH-1302
    //Updated divider from 3 to 2 for < 100 based on https://openroadmedia.atlassian.net/browse/ORMTECH-1410 to reduce number of ads
    //Updated logic based on https://openroadmedia.atlassian.net/browse/ORMTECH-1479
    //Updated logic based on https://openroadmedia.atlassian.net/browse/ORMTECH-1738
    if (totalComponent < 5) divider = 1;
    else if (totalComponent >= 5 && totalComponent <= 12) divider = 2;
    else if (totalComponent > 12 && totalComponent <= 20) divider = 3;
    else if (totalComponent > 20 && totalComponent <= 50) divider = 4;
    else if (totalComponent > 50 && totalComponent <= 70) divider = 5;
    else if (totalComponent > 70 && totalComponent <= 300) divider = 10;
    else if (totalComponent > 300) divider = 25;
  }

  //let adsAfter = Math.round(totalComponent / divider) -1 ;

  return Math.round(totalComponent / divider) - 1;
};
const BuildPage = (
  post,
  options,
  currentSiteID,
  isMobile,
  adNetwork,
  pageUrl,
  isTablet,
  classes,
  dealsData
) => {
  let page = [],
    len = post?.components.length,
    adsAfter,
    removeAds = post?.adSettings?.removeAds || false; //If ad is disable from Orion, don't push

  if (post?.components) {
    adsAfter = CalculateAdsAfterNumberOfComponent(len);
    let nextAdAtPosition;
    //console.log(`Total Components - ${len} => Ad after - ${adsAfter}`)
    for (let i = 0; i < len; i++) {
      page.push(
        <React.Fragment key={`component-${i}`}>
          {BuildComponent(post, i, currentSiteID, options, isMobile, pageUrl, classes, dealsData)}
        </React.Fragment>
      );
      //Insert Nativo div to second last component
      // if (i + 2 == len)
      // page.push(<div key={i + "nativo"} id="nativoincontent"></div>);
      // if (isAmp && (i + 1) % adsAfter == 0 && !removeAds)
      //   page.push(<GoogleAmpResonsiveAd key={i + 'google'} />);
      //For EBB, insert manual ad for all device type where as for other sites we only need manual ad on mobile device
      //(isMobile || currentSiteID === Sites.EARLYBIRDBOOKS) &&
      //console.log('i = ', i, '  nextAdAtPosition ', nextAdAtPosition)
      if (!removeAds && adsAfter > 0 && (nextAdAtPosition === i || i === 2)) {
        //console.log('I am pushing ad at - ', i, ' tablet ', isTablet, ' mobile ', isMobile)
        let sheKnowsAdType = isMobile || isTablet ? "mobileincontent" : "wideincontent";
        const postKey = String(post?.slug || post?._id || post?.title || "post").replace(
          /[^a-zA-Z0-9_-]/g,
          "-"
        );
        const adId = `skm-ad-${sheKnowsAdType}-${postKey}-${i}`;
        page.push(
          <ManualAd
            key={`manual-ad-${i}`}
            adNetwork={adNetwork}
            adType={sheKnowsAdType}
            adId={adId}
          />
        );
        nextAdAtPosition = i + adsAfter + 1;
        //console.log('Next Ad will be at ', nextAdAtPosition)
      }
    }
  }
  return page;
};

const BuildComponent = (post, key, currentSiteID, options = {}, _, pageUrl, classes, dealsData) => {
  const { data, deviceType, subscriberCookie, isUserComingFromNL, isPreview, isMobile, isTablet } =
      useAppContext(),
    { SiteSettings } = data || {},
    adnetwork = SiteSettings.ad_settings.adnetwork;

  const component = post?.components[key];
  const { type, props } = component ?? {};
  //NL display conditions
  let shouldDisplayNL = false;

  if (!subscriberCookie && !isUserComingFromNL) shouldDisplayNL = true;
  if (
    isPreview ||
    (props && props.isGiveaway) ||
    options.homePage ||
    options.categoryFunnel ||
    options.funnelPage
  )
    shouldDisplayNL = true;

  switch (type && type.toLowerCase()) {
    case "top-stories":
      return (
        <HomePageStories
          key={`${type}-${key}`}
          posts={props.posts}
          ImageSize={deviceType}
          DeviceSize={deviceType}
          type="topStories"
          isMobile={isMobile}
          adNetwork={adnetwork}
          currentSiteID={currentSiteID}
        />
      );

    case "trending-stories":
      return (
        <HomePageStories
          key={`${type}-${key}`}
          posts={props.posts}
          ImageSize={deviceType}
          DeviceSize={deviceType}
          type="trendingStories"
          isMobile={isMobile}
          adNetwork={adnetwork}
          currentSiteID={currentSiteID}
        />
      );
    case "recent-stories":
      return (
        <HomePageStories
          key={`${type}-${key}`}
          posts={props.posts}
          ImageSize={deviceType}
          DeviceSize={deviceType}
          type="recentStories"
          isMobile={isMobile}
          adNetwork={adnetwork}
          currentSiteID={currentSiteID}
        />
      );
    case "catalog-deals":
      return (
        <Deals
          key={`${type}-${key}`}
          ImageSize={deviceType}
          DeviceSize={deviceType}
          isMobile={isMobile}
          currentSiteID={currentSiteID}
          isTablet={isTablet}
          classes={classes}
          dealsData={dealsData}
        />
      );
    case "related-link":
      return <RelatedLink key={`${type}-${key}`} {...props} {...options} />;
    case "content":
      return <Content key={`${type}-${key}`} {...props} {...options} />;
    case "header":
      return <PostHeader key={`${type}-${key}`} {...props} {...options} type={type} />;
    case "sub-header":
      return <SubHeader key={`${type}-${key}`} {...props} {...options} type={type} />;

    case "book-listicle-info":
      return (
        <BookListicle
          key={`${type}-${key}`}
          {...props}
          {...options}
          currentSiteID={currentSiteID}
          imageSize={deviceType}
          type={type}
        />
      );
    case "book-info-batch":
      return (
        <BookInfoBatch
          key={`${type}-${key}`}
          components={props.bookInfoComponents}
          currentSiteID={currentSiteID}
          imageSize={deviceType}
          deviceSize={deviceType}
          {...options}
        />
      );
    case "book-excerpt":
      return (
        <div
          className={[styles["bookInfo"], "bookInfo"].join(" ")}
          key={`${type}-${key}`}
          id={`bookexcerpt-${props.index}`}
          style={{
            scrollMarginTop: "100px",
            scrollBehavior: "smooth",
          }}
        >
          <div className={[styles["book-excerpt-icon"], "book-excerpt-icon"].join(" ")}>
            <p>____________________</p>
            <NextImage
              ImageUrl={`${process.env.S3_IMAGE_URL}/orion-book-icon.png`}
              width={61}
              height={44}
              blockImage={false}
            ></NextImage>
            <p>____________________</p>
          </div>
          <BookInfo
            key={`${type}-${key}`}
            {...props}
            {...options}
            currentSiteID={currentSiteID}
            type={type}
          />
        </div>
      );
    case "interview":
      return <Interview key={`${type}-${key}`} {...props} {...options} />;
    case "pull-quote":
      return <PullQuote key={`${type}-${key}`} {...props} {...options} />;

    case "image":
      return (
        <PostImage
          key={`${type}-${key}`}
          {...props.image}
          {...options}
          size={deviceType}
          currentSiteID={currentSiteID}
          className="postImg"
        />
      );
    case "video": //RT:works
      return <Video key={`${type}-${key}`} {...props} {...options} />;
    case "listicle":
      return (
        <Listicle
          key={`${type}-${key}`}
          {...props}
          {...options}
          currentSiteID={currentSiteID}
          imageSize={deviceType}
          deviceSize={deviceType}
        />
      );
    case "affiliate-button":
      return (
        <AffiliateButton
          className="PageBuilderAf"
          key={`${type}-${key}`}
          {...props}
          {...options}
          imageSize={deviceType}
          deviceSize={deviceType}
          currentSiteID={currentSiteID}
        />
      );
    case "book-slider":
      return <BookSlider key={`${type}-${key}`} {...props} {...options} classes={classes} />;
    case "promo-ribbon":
      return <PromoRibbon key={`${type}-${key}`} {...props} {...options} parentClass={classes} />;
    case "author-slider":
      return <AuthorSlider key={`${type}-${key}`} {...props} {...options} classes={classes} />;
    case "newsletter-v2":
      switch (props.subComponentType) {
        case "inline":
          if (shouldDisplayNL) {
            if (props.modalWithImageProps.image.url) {
              return (
                <NewsletterV2WithImage
                  classNames="newsLettetPb"
                  key={`${type}-${key}`}
                  {...props}
                  {...options}
                  pageUrl={pageUrl}
                  siteId={currentSiteID}
                  isMobile={isMobile}
                />
              );
            } else {
              return (
                <NewsletterV2
                  classNames="newsLettetPb"
                  key={`${type}-${key}`}
                  {...props}
                  {...options}
                  pageUrl={pageUrl}
                  siteId={currentSiteID}
                  post_type={post?.post_type}
                  buttonBoxClasses={classes}
                />
              );
            }
          } else {
            return;
          }

        case "modal":
          if (shouldDisplayNL) {
            let newProps = { ...props };
            let nlModalProps = {
              hideCloseButton: true,
              disableToggleModal: true,
              disableRenderButton: props.disableRenderButton,
            };
            if (props.modalProps.nonClosableModal) {
              newProps = { ...newProps, ...nlModalProps };
            }
            return (
              <NewsletterV2Modal
                classNames="newsLettetPb"
                key={`${type}-${key}`}
                {...newProps}
                {...options}
                pageUrl={pageUrl}
                siteId={currentSiteID}
                isMobile={isMobile}
              />
            );
            // }
          } else return;
      }
      break;
    case "embed":
      return (
        <Embed
          key={`${type}-${key}`}
          post_id={post._id}
          componenentKey={key}
          classes={classes}
          {...props}
          {...options}
        />
      );
    case "quote-slider":
      return (
        <CardSlider
          key={`${type}-${key}`}
          slides={props.quotes}
          autoplaySpeed={6000}
          className={"quote-slider-wrapper"}
          cardType="quote"
          arrows={false}
          dots={true}
          imageSize={deviceType}
          deviceSize={deviceType}
        />
      );
    case "inline-quotes":
      return <InlineQuotes key={`${type}-${key}`} {...props} {...options} />;
    case "recommendation-engine-promos":
      if (options && options.books && options.books.length > 0) {
        return <RecommendationEnginePromos key={`${type}-${key}`} {...props} {...options} />;
      }
      break;
    case "deals-content":
      return <DealsContent key={`${type}-${key}`} {...props} {...options} />;
    default:
      break;
  }
};

export default PageBuilder;
